import {customElement, property, query} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import {ifDefined} from "lit/directives/if-defined.js";
import {GLOBAL} from "../../../common/globals";
import {DROPDOWN_EVENT, dropdownEvent, type DropdownEvent} from "./inputEvents";
import {prepareEvents} from "../../../common/utils/events";
import {done, fresh} from "../../../common/lifetime";
import {generateUuid} from "../../../common/crypto";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {Page} from "../../../common/page";
import {resolve} from "../../../container";
import {UnLitElement} from "../../../common/elements";
import {classMap} from "lit/directives/class-map.js";

export type DataSelectOption = {
    value: string;
    label: string;
    preset: boolean;
};

@customElement("eop-dropdown")
export class EopDropdown extends UnLitElement {

    @property({attribute: "input-id"})
    public inputId: string;
    @property({attribute: "label"})
    public label: string;
    @property({attribute: "placeholder"})
    public placeholder: string;
    @property({attribute: "options", type: Array})
    public options: DataSelectOption[] = [];
    @property({attribute: "form-uuid"})
    public formUUId: string;
    @property()
    public value: string = "";
    @property({attribute: true, reflect: true, type: Boolean})
    public open: boolean = false;
    @property({attribute: "invert-dropdown", reflect: true, type: Boolean})
    public invertDropdown: boolean = false;

    @query("select")
    private selectElement: HTMLSelectElement;
    @query(".custom-options")
    private customOptionsElement: HTMLDivElement;

    public constructor(private page: Page = resolve(Page)) {
        super();
        this.addEventListener(DROPDOWN_EVENT, (event: Event) => {
            const eventDetails = (event as DropdownEvent).detail;
            this.updateValue(eventDetails.value);
            this.close();
        });

        if (!this.formUUId) {
            this.formUUId = generateUuid();
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const preset = this.preset();
        if (preset && !this.value) {
            this.select(preset);
        }
    }

    public render(): TemplateResult {
        return html`
            ${this.renderLabel()}
            <div class="select-element">
                <select id=${this.formUUId}
                        name=${this.inputId}
                        aria-label=${ifDefined(this.label ?? this.placeholder)}
                        @change=${this.change}
                >
                    <option value="" ?selected=${!this.value}><span class="placeholder">${this.placeholder}</span></option>
                    ${this.renderNativeOptions()}
                </select>
                <div class="custom-select"
                     ?filled=${this.value}
                     @click=${this.toggle}
                     aria-hidden="true"
                >
                    <div class="custom-select-label">${this.renderSelectedLabel()}</div>
                    <div class="custom-options">
                        <div class="custom-options-scroll-container">
                            <eop-dropdown-option class="placeholder"
                                                 .label=${this.placeholder}
                                                 ?selected=${!this.value}
                                                 .value=${""}
                            ></eop-dropdown-option>
                            ${this.renderCustomOptions()}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private renderSelectedLabel() : TemplateResult {
        const classes = classMap({
            "placeholder": this.selectedValueDescriptor() === this.placeholder
        });
        return html`<span class=${classes}>${this.selectedValueDescriptor()}</span>`;
    }

    private renderLabel(): Nullable<TemplateResult> {
        if (!this.label || this.label.length === 0) {
            return null;
        }
        return html`
            <label for=${this.formUUId} @click=${this.preventLabelDefaultAction}>
                <span class="label-text">${this.label}</span>
            </label>
        `;
    }

    private renderCustomOptions(): TemplateResult[] {
        return this.options.map(option => html`
            <eop-dropdown-option
                    .value=${option.value}
                    .label=${option.label}
                    ?selected=${this.value === option.value}
            ></eop-dropdown-option>
        `);
    }

    private renderNativeOptions(): TemplateResult[] {
        return this.options.map(option => html`
            <option value=${option.value} ?selected=${this.value === option.value}>${option.label}</option>
        `);
    }

    public focusInput(): void {
        this.selectElement.focus();
    }

    protected preset(): Maybe<string> {
        return this.options
            .find(option => option.preset)
            ?.value;
    }

    private toggle(event: Event): void {
        if (this.open) {
            this.close();
        } else {
            this.openDropDown(event);
        }
    }

    private openDropDown(openingEvent: Event): void {
        this.updateDropDownPosition();
        this.open = true;
        prepareEvents(GLOBAL.bodyElement())
            .boundTo(fresh(this, "select"))
            .on("click", currentEvent => this.handleClickWhenOpened(currentEvent, openingEvent));
    }

    private updateDropDownPosition(): void {
        const dropdownRect = this.customOptionsElement.getBoundingClientRect();
        const viewportHeight = this.page.viewportHeight();
        const spaceBelow = viewportHeight - dropdownRect.bottom;

        this.invertDropdown = spaceBelow < 0;
    }

    private handleClickWhenOpened(currentEvent: Event, openingEvent: Event): void {
        if (currentEvent !== openingEvent) {
            this.close();
        }
    }

    private close(): void {
        this.open = false;
        this.invertDropdown = false;
        done(this, "select");
    }

    private selectedValueDescriptor(): string {
        return this.descriptorForValue(this.value);
    }

    private descriptorForValue(value: string): string {
        return this.options
                .find(option => value === option.value)
                ?.label
            ?? this.placeholder
            ?? "";
    }

    private select(value: string): void {
        this.updateValue(value);
        this.dispatchEvent(dropdownEvent(value, this.descriptorForValue(value)));
    }

    private updateValue(inputValue: string): void {
        if (this.value === inputValue) {
            return;
        }
        this.value = inputValue;
    }

    private change(event: Event): void {
        const value = (event.target as HTMLSelectElement).value;
        this.updateValue(value);
        this.dispatchEvent(dropdownEvent(value, this.descriptorForValue(value)));
    }

    private preventLabelDefaultAction(event: Event): void {
        return event.preventDefault();
    }
}